import React, { useState } from "react";
import { useGetReflectionAndTargets } from "../../Controllers/Goals/UserGoalController";
import SingleLineSkeletonLoader from "../SkeletonLoaders/SingleLineSkeletonLoader";

export const TargetReflectionModal = ({
  isOpen,
  onClose,
  userGoalId,
  params,
}) => {
  const [activeTab, setActiveTab] = useState("targets");
  const { data, isLoading, isError, error } = useGetReflectionAndTargets(
    userGoalId,
    params,
  );

  if (isLoading) {
    return <SingleLineSkeletonLoader />;
  }

  if (isError)
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#ff4d4f" }}>
        <p>Error: {error.message}</p>
      </div>
    );

  const targets = data?.targets || [];
  const rawReflections = data?.reflections || [];

  // Flatten reflections - extract follow-up questions to same level
  const flattenReflections = (reflections) => {
    const flattened = [];

    reflections.forEach((reflection) => {
      // Add main question and answer
      flattened.push({
        question: reflection.question,
        answer: reflection.answer,
      });

      // Add follow-up questions if they exist
      if (reflection.follow_up && reflection.follow_up.length > 0) {
        reflection.follow_up.forEach((followUp) => {
          flattened.push({
            question: followUp.question,
            answer: followUp.answer,
          });
        });
      }
    });

    return flattened;
  };

  const reflections = flattenReflections(rawReflections);

  // Function to render HTML content safely
  const renderHtmlContent = (content) => {
    if (!content) return "No content available";

    // Check if content contains HTML tags
    if (
      content.includes("<p>") ||
      content.includes("<div>") ||
      content.includes("<br>")
    ) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    return content;
  };

  const renderTargets = () => {
    if (targets.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "20px", color: "#e6ecf1" }}>
          <p>No targets available for this goal.</p>
        </div>
      );
    }

    return (
      <div style={{ padding: "16px 0", maxHeight: "400px", overflowY: "auto" }}>
        {targets.map((target, index) => (
          <div key={index}>
            <div
              style={{
                padding: "16px",
                marginBottom: "12px",
              }}
            >
              <div style={{ color: "#e6ecf1", margin: "0", lineHeight: "1.5" }}>
                {renderHtmlContent(target)}
              </div>
            </div>
            {/* Add divider between targets, but not after the last one */}
            {index < targets.length - 1 && (
              <div
                style={{
                  height: "1px",
                  backgroundColor: "rgba(0, 140, 255, 0.3)",
                  margin: "16px 0",
                }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderReflections = () => {
    if (reflections.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "20px", color: "#e6ecf1" }}>
          <p>No reflections available for this goal.</p>
        </div>
      );
    }

    return (
      <div style={{ padding: "16px 0", maxHeight: "400px", overflowY: "auto" }}>
        {reflections.map((reflection, index) => (
          <div key={index}>
            <div
              style={{
                padding: "16px",
                marginBottom: "16px",
              }}
            >
              <div style={{ marginBottom: "12px" }}>
                <p
                  style={{
                    color: "#e6ecf1",
                    margin: "0 0 8px 0",
                    fontWeight: "500",
                  }}
                >
                  {reflection.question || "No question available"}
                </p>
                <div
                  style={{ color: "#e6ecf1", margin: "0", lineHeight: "1.5" }}
                >
                  {renderHtmlContent(
                    reflection.answer || "No answer available",
                  )}
                </div>
              </div>
            </div>
            {/* Add divider between reflections, but not after the last one */}
            {index < reflections.length - 1 && (
              <div
                style={{
                  height: "1px",
                  backgroundColor: "rgba(0, 140, 255, 0.3)",
                  margin: "16px 0",
                }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid rgba(0, 140, 255, 0.3)",
          marginBottom: "20px",
          justifyContent: "space-between",
        }}
      >
        <button
          onClick={() => setActiveTab("targets")}
          style={{
            background:
              activeTab === "targets"
                ? "rgba(0, 140, 255, 0.2)"
                : "transparent",
            border: "none",
            color: activeTab === "targets" ? "#008cff" : "#e6ecf1",
            padding: "12px 24px",
            cursor: "pointer",
            borderBottom:
              activeTab === "targets"
                ? "2px solid #008cff"
                : "2px solid transparent",
            transition: "all 0.2s ease",
            width: "50%",
          }}
        >
          Targets ({targets.length})
        </button>
        <button
          onClick={() => setActiveTab("reflections")}
          style={{
            background:
              activeTab === "reflections"
                ? "rgba(0, 140, 255, 0.2)"
                : "transparent",
            border: "none",
            color: activeTab === "reflections" ? "#008cff" : "#e6ecf1",
            padding: "12px 24px",
            cursor: "pointer",
            borderBottom:
              activeTab === "reflections"
                ? "2px solid #008cff"
                : "2px solid transparent",
            transition: "all 0.2s ease",
            width: "50%",
          }}
        >
          Reflections ({reflections.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "targets" ? renderTargets() : renderReflections()}
    </div>
  );
};
